import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Search, ChevronDown, X, Loader2 } from 'lucide-react';
import { getDropdownPosition } from '../utils/dropdownPosition';

export interface SearchableSelectOption {
  value: number | string;
  label: string;
  description?: string;
}

interface SearchableSelectProps {
  options: SearchableSelectOption[];
  value: number | string;
  onChange: (value: number | string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
  clearValue?: number | string;
  /** When provided, the component calls this instead of filtering internally.
   *  Pass filtered options back via the `options` prop. */
  onSearchChange?: (search: string) => void;
  isLoading?: boolean;
}

const SearchableSelect = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No results found',
  className = '',
  disabled = false,
  clearValue = 0,
  onSearchChange,
  isLoading = false,
}: SearchableSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const keyboardFocusRef = useRef(false);

  const selectedOption = options.find((opt) => String(opt.value) === String(value));
  const isEmpty =
    value === clearValue ||
    value === 0 ||
    value === '' ||
    value === null ||
    value === undefined;

  // When onSearchChange is provided the parent owns filtering; use options as-is.
  const filteredOptions = onSearchChange
    ? options
    : options.filter((opt) => {
        const query = search.toLowerCase().trim();
        if (!query) return true;
        return (
          opt.label.toLowerCase().includes(query) ||
          (opt.description?.toLowerCase().includes(query) ?? false)
        );
      });

  const updateDropdownPosition = useCallback(() => {
    if (!containerRef.current) return;
    setDropdownStyle(getDropdownPosition(containerRef.current.getBoundingClientRect()));
  }, []);

  useEffect(() => {
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') keyboardFocusRef.current = true;
    };
    const handlePointerDown = () => {
      keyboardFocusRef.current = false;
    };
    document.addEventListener('keydown', handleTabKey);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleTabKey);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearch('');
      onSearchChange?.('');
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();
    inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(updateDropdownPosition);

    const handleReposition = () => updateDropdownPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [isOpen, updateDropdownPosition]);

  const handleSearch = (val: string) => {
    setSearch(val);
    onSearchChange?.(val);
  };

  const handleOpen = (initialSearch = '') => {
    if (disabled) return;
    setIsOpen(true);
    setSearch(initialSearch);
    onSearchChange?.(initialSearch);
  };

  const handleSelect = (optionValue: number | string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearch('');
    onSearchChange?.('');
  };

  const handleClear = (e: ReactMouseEvent) => {
    e.stopPropagation();
    onChange(clearValue);
    setSearch('');
    onSearchChange?.('');
  };

  const handleTriggerFocus = () => {
    if (disabled || isOpen) return;
    if (keyboardFocusRef.current) {
      keyboardFocusRef.current = false;
      handleOpen();
    }
  };

  const handleTriggerKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleOpen();
      return;
    }

    if (!isOpen && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      e.preventDefault();
      handleOpen(e.key);
    }
  };

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden flex flex-col"
    >
      <div className="p-2 border-b border-gray-100 shrink-0">
        <div className="relative">
          {isLoading ? (
            <Loader2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
          ) : (
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          )}
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-slate-800 focus:border-slate-600"
          />
        </div>
      </div>

      <ul className="overflow-y-auto py-1 flex-1 min-h-0">
        {isLoading ? (
          <li className="px-3 py-2 text-sm text-gray-400 italic">Searching…</li>
        ) : filteredOptions.length === 0 ? (
          <li className="px-3 py-2 text-sm text-gray-500">
            {search.trim() ? emptyMessage : 'Type to search…'}
          </li>
        ) : (
          filteredOptions.map((opt) => (
            <li key={opt.value}>
              <button
                type="button"
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 transition-colors ${
                  String(opt.value) === String(value)
                    ? 'bg-slate-50 text-slate-700 font-medium'
                    : 'text-gray-900'
                }`}
              >
                <span className="block truncate">{opt.label}</span>
                {opt.description && (
                  <span className="block text-xs text-gray-500 truncate">{opt.description}</span>
                )}
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        onFocus={handleTriggerFocus}
        onKeyDown={handleTriggerKeyDown}
        className={`w-full flex items-center justify-between gap-2 border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-slate-800 focus:border-slate-600 ${
          disabled
            ? 'opacity-60 cursor-not-allowed bg-gray-50'
            : 'hover:border-slate-400'
        }`}
      >
        <span className={isEmpty ? 'text-gray-400 truncate' : 'text-gray-900 truncate'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isEmpty && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onChange(clearValue);
                  setSearch('');
                }
              }}
              className="p-0.5 text-gray-400 hover:text-gray-600 rounded"
              aria-label="Clear selection"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {dropdown && createPortal(dropdown, document.body)}
    </div>
  );
};

export default SearchableSelect;
